import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatTicketPrice,
  formatEventDateLabel,
  formatEventTimeLabel,
  normalizeEvent,
  seededPublicEvents,
  slugifyEventTitle,
} from '../data/events';
import { useEventManager } from '../hooks/useEventManager';
import { useSiteAuth } from '../hooks/useSiteAuth';
import { listBlissEventImages, uploadBlissEventImage, type UploadedEventImage } from '../services/events-api';
import type { EventTicketType, PublicEvent } from '../types';

function createDraftTicket(): EventTicketType {
  const seed = Date.now().toString(36);

  return {
    id: `ticket-${seed}-${Math.random().toString(36).slice(2, 6)}`,
    name: 'New Ticket Type',
    category: 'participant',
    participantType: 'general',
    price: null,
    priceLabel: 'Coming Soon',
    description: 'Add the participation details for this ticket type.',
    status: 'coming-soon',
  };
}

function cloneEvent(event: PublicEvent): PublicEvent {
  return {
    ...event,
    galleryImages: [...event.galleryImages],
    tickets: event.tickets.map((ticket) => ({ ...ticket })),
    highlights: [...event.highlights],
  };
}

function dedupeImageList(images: string[]): string[] {
  const seen = new Set<string>();

  return images
    .map((image) => image.trim())
    .filter((image) => {
      if (!image || seen.has(image)) {
        return false;
      }

      seen.add(image);
      return true;
    });
}

function buildGalleryImages(coverImage: string, images: string[]): string[] {
  const nextImages = dedupeImageList(images);
  const nextCover = coverImage.trim();

  if (!nextCover) {
    return nextImages;
  }

  return dedupeImageList([nextCover, ...nextImages.filter((image) => image !== nextCover)]);
}

export default function PortalEvents() {
  const { session } = useSiteAuth();
  const {
    isLoading,
    syncError,
    storageMode,
    events,
    publishedEvents,
    featuredEvent,
    createEvent,
    saveEvent,
    deleteEvent,
    setFeaturedEvent,
    resetEvents,
  } = useEventManager();
  const canManageEvents = session?.role === 'admin';
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [draftEvent, setDraftEvent] = useState<PublicEvent | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const [availableImages, setAvailableImages] = useState<UploadedEventImage[]>([]);
  const [isLoadingImageLibrary, setIsLoadingImageLibrary] = useState(false);
  const [assetPickerMode, setAssetPickerMode] = useState<'cover' | 'gallery' | null>(null);

  useEffect(() => {
    if (!events.length) {
      if (draftEvent && (!selectedEventId || draftEvent.id === selectedEventId)) {
        return;
      }

      setSelectedEventId(null);
      setDraftEvent(null);
      return;
    }

    if (!selectedEventId) {
      setSelectedEventId(events[0].id);
      setDraftEvent(cloneEvent(events[0]));
      return;
    }

    const selectedEvent = events.find((event) => event.id === selectedEventId);
    if (!selectedEvent) {
      if (draftEvent && draftEvent.id === selectedEventId) {
        return;
      }

      setSelectedEventId(events[0].id);
      setDraftEvent(cloneEvent(events[0]));
      return;
    }

    if (!draftEvent || draftEvent.id !== selectedEventId) {
      setDraftEvent(cloneEvent(selectedEvent));
    }
  }, [draftEvent, events, selectedEventId]);

  useEffect(() => {
    let isActive = true;

    async function loadImageLibrary() {
      if (!canManageEvents || storageMode !== 'bliss') {
        if (isActive) {
          setAvailableImages([]);
          setAssetPickerMode(null);
          setIsLoadingImageLibrary(false);
        }
        return;
      }

      if (isActive) {
        setIsLoadingImageLibrary(true);
      }

      try {
        const images = await listBlissEventImages();
        if (isActive) {
          setAvailableImages(images);
        }
      } catch (error) {
        if (isActive) {
          setFeedback(error instanceof Error ? error.message : 'Unable to load the uploaded image library right now.');
        }
      } finally {
        if (isActive) {
          setIsLoadingImageLibrary(false);
        }
      }
    }

    void loadImageLibrary();

    return () => {
      isActive = false;
    };
  }, [canManageEvents, storageMode]);

  if (!session) {
    return null;
  }

  function updateDraft(updates: Partial<PublicEvent>) {
    setDraftEvent((current) => (current ? { ...current, ...updates } : current));
    setFeedback(null);
  }

  function updateDraftDate(eventDate: string) {
    updateDraft({
      eventDate,
      dateLabel: eventDate ? formatEventDateLabel(eventDate) : 'Date to be announced',
    });
  }

  function updateDraftTime(eventTime: string) {
    updateDraft({
      eventTime,
      timeLabel: eventTime ? formatEventTimeLabel(eventTime) : 'Time to be announced',
    });
  }

  function updateDraftTicket(ticketId: string, updates: Partial<EventTicketType>) {
    setDraftEvent((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        tickets: current.tickets.map((ticket) => {
          if (ticket.id !== ticketId) {
            return ticket;
          }

          const nextTicket = { ...ticket, ...updates };
          return {
            ...nextTicket,
            participantType:
              nextTicket.category === 'participant'
                ? (nextTicket.participantType ?? 'general')
                : undefined,
            priceLabel: formatTicketPrice(nextTicket.price, nextTicket),
          };
        }),
      };
    });
    setFeedback(null);
  }

  function updateHighlights(value: string) {
    const nextHighlights = value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    updateDraft({
      highlights: nextHighlights.length ? nextHighlights : ['Add event highlights here.'],
    });
  }

  function updateCoverImage(nextCoverImage: string) {
    setDraftEvent((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        heroImage: nextCoverImage,
        galleryImages: buildGalleryImages(nextCoverImage, current.galleryImages),
      };
    });
    setFeedback(null);
  }

  function addGalleryImages(images: string[]) {
    setDraftEvent((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        galleryImages: buildGalleryImages(current.heroImage, [...current.galleryImages, ...images]),
      };
    });
    setFeedback(null);
  }

  function removeEventImage(imageToRemove: string) {
    setDraftEvent((current) => {
      if (!current) {
        return current;
      }

      const remainingImages = current.galleryImages.filter((image) => image !== imageToRemove);
      const nextHeroImage =
        current.heroImage === imageToRemove
          ? (remainingImages[0] ?? '')
          : current.heroImage;

      return {
        ...current,
        heroImage: nextHeroImage,
        galleryImages: buildGalleryImages(nextHeroImage, remainingImages),
      };
    });
    setFeedback('Image removed from this event. Save the event to keep the change.');
  }

  function clearAllEventImages() {
    setDraftEvent((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        heroImage: '',
        galleryImages: [],
      };
    });
    setFeedback('All images were removed from this event. Save the event to keep the change.');
  }

  function handleSelectExistingImage(imageUrl: string, kind: 'cover' | 'gallery') {
    if (kind === 'cover') {
      updateCoverImage(imageUrl);
      setFeedback('Selected image set as the cover photo. Save the event to keep the change.');
      return;
    }

    addGalleryImages([imageUrl]);
    setFeedback('Selected image added to the event gallery. Save the event to keep the change.');
  }

  async function handleImageUpload(files: FileList | null, kind: 'cover' | 'gallery') {
    if (!draftEvent || !files?.length) {
      return;
    }

    if (storageMode !== 'bliss') {
      setFeedback('Photo uploads are available when the portal is connected to Bliss storage.');
      return;
    }

    const uploads = Array.from(files);

    try {
      if (kind === 'cover') {
        setIsUploadingCover(true);
        const uploaded = await uploadBlissEventImage(draftEvent.id, uploads[0], 'cover');
        updateCoverImage(uploaded.url);
        setAvailableImages((current) => dedupeUploadedImages([uploaded, ...current]));
        setFeedback('Cover image uploaded. Save the event to keep it attached to this event.');
        return;
      }

      setIsUploadingGallery(true);
      const uploadedImages: string[] = [];

      for (const file of uploads) {
        const uploaded = await uploadBlissEventImage(draftEvent.id, file, 'gallery');
        uploadedImages.push(uploaded.url);
        setAvailableImages((current) => dedupeUploadedImages([uploaded, ...current]));
      }

      addGalleryImages(uploadedImages);
      setFeedback(
        `${uploadedImages.length} ${uploadedImages.length === 1 ? 'gallery image was' : 'gallery images were'} uploaded. Save the event to keep ${uploadedImages.length === 1 ? 'it' : 'them'} attached to this event.`,
      );
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to upload event images right now.');
    } finally {
      setIsUploadingCover(false);
      setIsUploadingGallery(false);
    }
  }

  function toggleAssetPicker(nextMode: 'cover' | 'gallery') {
    setAssetPickerMode((current) => (current === nextMode ? null : nextMode));
  }

  function handleCreateEvent() {
    const nextEvent = createEvent();
    setSelectedEventId(nextEvent.id);
    setDraftEvent(cloneEvent(nextEvent));
    setFeedback('New event created. Add the details and save when you are ready.');
  }

  function handleSelectEvent(event: PublicEvent) {
    setSelectedEventId(event.id);
    setDraftEvent(cloneEvent(event));
    setFeedback(null);
  }

  async function handleSaveEvent() {
    if (!draftEvent) {
      return;
    }

    const normalizedDraft = normalizeEvent({
      ...draftEvent,
      slug: slugifyEventTitle(draftEvent.slug || draftEvent.title) || draftEvent.id,
    });

    try {
      setIsWorking(true);
      const savedEvent = await saveEvent(normalizedDraft);
      setDraftEvent(savedEvent);
      setSelectedEventId(savedEvent.id);
      setFeedback(storageMode === 'bliss' ? 'Event saved to Bliss.' : 'Event saved locally.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to save the event right now.');
    } finally {
      setIsWorking(false);
    }
  }

  async function handleTogglePublished() {
    if (!draftEvent) {
      return;
    }

    const nextEvent = normalizeEvent({
      ...draftEvent,
      isPublished: !draftEvent.isPublished,
    });

    try {
      setIsWorking(true);
      const savedEvent = await saveEvent(nextEvent);
      setDraftEvent(savedEvent);
      setFeedback(savedEvent.isPublished ? 'Event published.' : 'Event unpublished.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to change publish state right now.');
    } finally {
      setIsWorking(false);
    }
  }

  async function handleFeatureEvent() {
    if (!draftEvent) {
      return;
    }

    if (!draftEvent.isPublished) {
      setFeedback('Publish the event before making it the featured event.');
      return;
    }

    try {
      setIsWorking(true);
      await setFeaturedEvent(draftEvent.id);
      setDraftEvent({
        ...draftEvent,
        featured: true,
        isPublished: true,
      });
      setFeedback('Featured event updated.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to feature the event right now.');
    } finally {
      setIsWorking(false);
    }
  }

  async function handleDeleteEvent() {
    if (!draftEvent) {
      return;
    }

    try {
      setIsWorking(true);
      const remainingEvents = events.filter((event) => event.id !== draftEvent.id);
      await deleteEvent(draftEvent.id);
      setSelectedEventId(remainingEvents[0]?.id ?? null);
      setDraftEvent(remainingEvents[0] ? cloneEvent(remainingEvents[0]) : null);
      setFeedback('Event removed.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to delete the event right now.');
    } finally {
      setIsWorking(false);
    }
  }

  async function handleResetEvents() {
    try {
      setIsWorking(true);
      await resetEvents();
      setSelectedEventId(seededPublicEvents[0]?.id ?? null);
      setDraftEvent(seededPublicEvents[0] ? cloneEvent(normalizeEvent(seededPublicEvents[0])) : null);
      setFeedback(
        storageMode === 'bliss'
          ? 'Events reset in Bliss to the project seed set.'
          : 'Events reset to the project seed set.',
      );
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to reset the events right now.');
    } finally {
      setIsWorking(false);
    }
  }

  const attachedImageCount = draftEvent?.galleryImages.filter(Boolean).length ?? 0;
  const previewImage = draftEvent?.heroImage || draftEvent?.galleryImages.find(Boolean) || '';
  const schedulePreview = draftEvent ? `${draftEvent.dateLabel} / ${draftEvent.timeLabel}` : 'Date to be announced / Time to be announced';

  return (
    <motion.div
      className="portal-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <section className="portal-section portal-events-workspace-bar">
        <div className="portal-events-workspace-shell">
          <div className="portal-events-workspace-copy">
            <p className="info-kicker">Events workspace</p>
            <h1>Build, schedule, and publish each event from one editor.</h1>
            <p className="portal-events-workspace-copy-text">
              Move through saved events from the left rail, then shape the public-facing poster, media, ticket mix, and publish controls in one place.
            </p>
          </div>

          <div className="portal-events-workspace-actions">
            {canManageEvents ? (
              <button type="button" onClick={handleCreateEvent} className="button-primary tap-target" disabled={isWorking}>
                Create event
              </button>
            ) : null}
            <Link to="/events" className="button-secondary tap-target">
              Open public events hub
            </Link>
          </div>
        </div>

        {syncError ? (
          <div className="auth-runtime-note">
            <p className="info-kicker">Storage mode</p>
            <p>{storageMode === 'bliss' ? syncError : `${syncError} Changes are currently staying local.`}</p>
          </div>
        ) : null}

        <div className="portal-events-metrics">
          <article className="portal-events-metric-card">
            <span>Total events</span>
            <strong>{isLoading ? '...' : events.length}</strong>
          </article>
          <article className="portal-events-metric-card">
            <span>Published</span>
            <strong>{isLoading ? '...' : publishedEvents.length}</strong>
          </article>
          <article className="portal-events-metric-card">
            <span>Featured event</span>
            <strong>{isLoading ? 'Loading...' : (featuredEvent?.title ?? 'None yet')}</strong>
          </article>
          <article className="portal-events-metric-card">
            <span>Storage</span>
            <strong>{storageMode === 'bliss' ? 'Bliss sync' : 'Local fallback'}</strong>
          </article>
        </div>
      </section>

      <div className="portal-event-admin-grid">
        <section className="portal-section portal-event-manager">
          <div className="portal-event-rail-header">
            <div>
              <p className="info-kicker">Saved events</p>
              <h2>Current events</h2>
              <p className="portal-event-rail-copy">
                {events.length
                  ? `${publishedEvents.length} published / ${events.length} total`
                  : 'Create the first event to begin the live workflow.'}
              </p>
            </div>
          </div>

          {events.length ? (
            <div className="portal-admin-event-list">
              {events.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => handleSelectEvent(event)}
                  className={`portal-admin-event-card ${selectedEventId === event.id ? 'portal-admin-event-card--active' : ''}`}
                >
                  <div className="portal-admin-event-card-row">
                    <strong>{event.title}</strong>
                    <span className={`portal-publish-badge ${event.isPublished ? 'portal-publish-badge--live' : ''}`}>
                      {event.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p>{event.shortDescription}</p>
                  <div className="portal-admin-event-meta">
                    <span>{event.dateLabel}</span>
                    <span>{event.timeLabel}</span>
                    <span>{event.status.replace('-', ' ')}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="auth-runtime-note">
              <p className="info-kicker">No saved events</p>
              <p>Create the first event and it will appear here as your working rail.</p>
            </div>
          )}

          {canManageEvents ? (
            <div className="portal-event-rail-actions">
              <button type="button" onClick={handleResetEvents} className="button-ghost tap-target" disabled={isWorking}>
                Reset to seeded events
              </button>
            </div>
          ) : null}
        </section>

        <section className="portal-section portal-event-editor">
          {draftEvent ? (
            <>
              <div className="portal-section-heading portal-section-heading--editor">
                <div>
                  <p className="info-kicker">Event editor</p>
                  <h2>{draftEvent.title}</h2>
                  <p className="portal-event-editor-copy">
                    Refine the public-facing details first, then attach media, ticket paths, and publish controls.
                  </p>
                </div>
                <div className="portal-inline-actions">
                  <Link to={draftEvent.isPublished ? `/events/${draftEvent.slug}` : '/events'} className="button-ghost tap-target">
                    {draftEvent.isPublished ? 'Preview event page' : 'View public hub'}
                  </Link>
                </div>
              </div>

              {!canManageEvents ? (
                <div className="auth-runtime-note">
                  <p className="info-kicker">Read only</p>
                  <p>Only admins can change event setup. Other roles can review the current event configuration here.</p>
                </div>
              ) : null}

              {feedback ? <div className="auth-success">{feedback}</div> : null}

              <div className="portal-event-preview">
                <div className={`portal-event-preview-media ${previewImage ? 'portal-event-preview-media--image' : ''}`}>
                  {previewImage ? <img src={previewImage} alt={`${draftEvent.title} preview`} /> : null}
                  <div className="portal-event-preview-overlay" />
                  <div className="portal-event-preview-status">
                    <span className={`portal-publish-badge ${draftEvent.isPublished ? 'portal-publish-badge--live' : ''}`}>
                      {draftEvent.isPublished ? 'Published' : 'Draft'}
                    </span>
                    {featuredEvent?.id === draftEvent.id ? (
                      <span className="portal-publish-badge portal-publish-badge--feature">Featured</span>
                    ) : null}
                  </div>
                  <div className="portal-event-preview-copy">
                    <p className="info-kicker">Live poster preview</p>
                    <h3>{draftEvent.title}</h3>
                    <p>{draftEvent.shortDescription}</p>
                    <div className="portal-event-preview-meta">
                      <span>{schedulePreview}</span>
                      <span>{draftEvent.locationName}</span>
                    </div>
                  </div>
                </div>

                <div className="portal-event-preview-summary">
                  <div className="portal-event-preview-stat">
                    <span>Ticket types</span>
                    <strong>{draftEvent.tickets.length}</strong>
                  </div>
                  <div className="portal-event-preview-stat">
                    <span>Attached images</span>
                    <strong>{attachedImageCount}</strong>
                  </div>
                  <div className="portal-event-preview-stat">
                    <span>Status</span>
                    <strong>{draftEvent.status.replace('-', ' ')}</strong>
                  </div>
                </div>
              </div>

              <div className="portal-event-editor-block">
                <div className="portal-section-heading portal-section-heading--compact">
                  <div>
                    <p className="info-kicker">Event details</p>
                    <h3>Headline, schedule, location, and public copy.</h3>
                  </div>
                </div>

              <div className="portal-form-grid portal-form-grid--events">
                <label className="auth-field">
                  <span>Event title</span>
                  <input
                    className="auth-input"
                    type="text"
                    value={draftEvent.title}
                    onChange={(event) =>
                      updateDraft({
                        title: event.target.value,
                        slug: slugifyEventTitle(event.target.value) || draftEvent.id,
                      })
                    }
                    disabled={!canManageEvents}
                  />
                </label>

                <label className="auth-field">
                  <span>Date</span>
                  <input
                    className="auth-input"
                    type="date"
                    value={draftEvent.eventDate || ''}
                    onChange={(event) => updateDraftDate(event.target.value)}
                    disabled={!canManageEvents}
                  />
                </label>

                <label className="auth-field">
                  <span>Time</span>
                  <input
                    className="auth-input"
                    type="time"
                    value={draftEvent.eventTime || ''}
                    onChange={(event) => updateDraftTime(event.target.value)}
                    disabled={!canManageEvents}
                  />
                </label>

                <div className="auth-runtime-note portal-field-span-full">
                  <p className="info-kicker">Event schedule preview</p>
                  <p>{draftEvent.dateLabel} · {draftEvent.timeLabel}</p>
                </div>

                <label className="auth-field">
                  <span>Location name</span>
                  <input
                    className="auth-input"
                    type="text"
                    value={draftEvent.locationName}
                    onChange={(event) => updateDraft({ locationName: event.target.value })}
                    disabled={!canManageEvents}
                  />
                </label>

                <label className="auth-field">
                  <span>Location address</span>
                  <input
                    className="auth-input"
                    type="text"
                    value={draftEvent.locationAddress}
                    onChange={(event) => updateDraft({ locationAddress: event.target.value })}
                    disabled={!canManageEvents}
                  />
                </label>

                <label className="auth-field">
                  <span>Public status</span>
                  <select
                    className="auth-input"
                    value={draftEvent.status}
                    onChange={(event) => updateDraft({ status: event.target.value as PublicEvent['status'] })}
                    disabled={!canManageEvents}
                  >
                    <option value="coming-soon">Coming soon</option>
                    <option value="open">Open</option>
                    <option value="past">Past</option>
                  </select>
                </label>

                <label className="auth-field portal-field-span-full">
                  <span>Short description</span>
                  <textarea
                    className="auth-input auth-input--textarea"
                    value={draftEvent.shortDescription}
                    onChange={(event) => updateDraft({ shortDescription: event.target.value })}
                    disabled={!canManageEvents}
                    rows={3}
                  />
                </label>

                <label className="auth-field portal-field-span-full">
                  <span>Full description</span>
                  <textarea
                    className="auth-input auth-input--textarea"
                    value={draftEvent.fullDescription}
                    onChange={(event) => updateDraft({ fullDescription: event.target.value })}
                    disabled={!canManageEvents}
                    rows={5}
                  />
                </label>

                <label className="auth-field portal-field-span-full">
                  <span>Highlights</span>
                  <textarea
                    className="auth-input auth-input--textarea"
                    value={draftEvent.highlights.join('\n')}
                    onChange={(event) => updateHighlights(event.target.value)}
                    disabled={!canManageEvents}
                    rows={4}
                  />
                </label>
              </div>

              </div>

              <div className="portal-event-editor-block portal-event-image-preview">
                <div className="portal-section-heading">
                  <div>
                    <p className="info-kicker">Event images</p>
                    <h3>Cover image and additional gallery photos.</h3>
                  </div>
                </div>

                <div className="portal-upload-actions">
                  <label
                    className={`button-secondary tap-target portal-upload-button ${!canManageEvents || storageMode !== 'bliss' ? 'portal-upload-button--disabled' : ''}`}
                  >
                    <input
                      className="portal-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                      disabled={!canManageEvents || storageMode !== 'bliss' || isUploadingCover || isUploadingGallery}
                      onChange={(event) => {
                        void handleImageUpload(event.currentTarget.files, 'cover');
                        event.currentTarget.value = '';
                      }}
                    />
                    {isUploadingCover ? 'Uploading cover...' : 'Upload cover photo'}
                  </label>

                  <button
                    type="button"
                    className={`button-ghost tap-target ${assetPickerMode === 'cover' ? 'portal-image-toggle--active' : ''}`}
                    onClick={() => toggleAssetPicker('cover')}
                    disabled={!canManageEvents || storageMode !== 'bliss' || isUploadingCover || isUploadingGallery}
                  >
                    {assetPickerMode === 'cover' ? 'Hide cover library' : 'Select cover'}
                  </button>

                  <label
                    className={`button-secondary tap-target portal-upload-button ${!canManageEvents || storageMode !== 'bliss' ? 'portal-upload-button--disabled' : ''}`}
                  >
                    <input
                      className="portal-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                      multiple
                      disabled={!canManageEvents || storageMode !== 'bliss' || isUploadingCover || isUploadingGallery}
                      onChange={(event) => {
                        void handleImageUpload(event.currentTarget.files, 'gallery');
                        event.currentTarget.value = '';
                      }}
                    />
                    {isUploadingGallery ? 'Uploading gallery...' : 'Upload gallery photos'}
                  </label>

                  <button
                    type="button"
                    className={`button-ghost tap-target ${assetPickerMode === 'gallery' ? 'portal-image-toggle--active' : ''}`}
                    onClick={() => toggleAssetPicker('gallery')}
                    disabled={!canManageEvents || storageMode !== 'bliss' || isUploadingCover || isUploadingGallery}
                  >
                    {assetPickerMode === 'gallery' ? 'Hide gallery library' : 'Select gallery'}
                  </button>
                </div>

                <p className="portal-upload-note">
                  Upload photos directly into Bliss storage, reuse an image that has already been uploaded, or remove an image from this event if it was added by mistake.
                </p>

                {assetPickerMode ? (
                  <div className="portal-image-library">
                    <div className="portal-section-heading">
                      <div>
                        <p className="info-kicker">Uploaded image library</p>
                        <h3>{assetPickerMode === 'cover' ? 'Select a cover photo.' : 'Select gallery photos.'}</h3>
                      </div>
                    </div>

                    {isLoadingImageLibrary ? (
                      <div className="auth-runtime-note">
                        <p>Loading uploaded images...</p>
                      </div>
                    ) : availableImages.length ? (
                      <div className="portal-event-image-grid">
                        {availableImages.map((image) => {
                          const alreadyAttached = draftEvent.galleryImages.includes(image.url);
                          const isCover = draftEvent.heroImage === image.url;

                          return (
                            <figure key={image.storageKey} className="portal-event-image-card">
                              <img src={image.url} alt={image.name} />
                              <figcaption>
                                <span>{image.name}</span>
                                <button
                                  type="button"
                                  className="button-ghost tap-target portal-image-card-action"
                                  onClick={() => handleSelectExistingImage(image.url, assetPickerMode)}
                                  disabled={assetPickerMode === 'cover' ? isCover : alreadyAttached}
                                >
                                  {assetPickerMode === 'cover'
                                    ? (isCover ? 'Current cover' : 'Use as cover')
                                    : (alreadyAttached ? 'Already added' : 'Add to gallery')}
                                </button>
                              </figcaption>
                            </figure>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="auth-runtime-note">
                        <p>No uploaded images are available yet. Upload a photo first to reuse it later.</p>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="portal-event-image-grid">
                  {draftEvent.galleryImages.filter(Boolean).length ? (
                    <>
                      <div className="portal-section-heading portal-section-heading--compact portal-field-span-full">
                        <div>
                          <p className="info-kicker">Current event images</p>
                          <h3>Photos currently attached to this event.</h3>
                        </div>
                        {canManageEvents ? (
                          <div className="portal-inline-actions">
                            {draftEvent.heroImage ? (
                              <button
                                type="button"
                                className="button-ghost tap-target portal-danger-button"
                                onClick={() => removeEventImage(draftEvent.heroImage)}
                              >
                                Remove cover photo
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="button-ghost tap-target portal-danger-button"
                              onClick={clearAllEventImages}
                            >
                              Clear all event images
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {draftEvent.galleryImages.filter(Boolean).map((image, index) => (
                        <figure
                          key={`${image}-${index}`}
                          className={`portal-event-image-card ${index === 0 ? 'portal-event-image-card--cover' : ''}`}
                        >
                          <img src={image} alt={index === 0 ? `${draftEvent.title} cover image` : `${draftEvent.title} gallery image ${index}`} />
                          <figcaption>
                            <span>{index === 0 ? 'Cover image' : `Additional image ${index}`}</span>
                            {canManageEvents ? (
                              <span className="portal-image-card-actions">
                                {index !== 0 ? (
                                  <button
                                    type="button"
                                    className="button-ghost tap-target portal-image-card-action"
                                    onClick={() => updateCoverImage(image)}
                                  >
                                    Make cover
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="button-ghost tap-target portal-image-card-action portal-danger-button"
                                  onClick={() => removeEventImage(image)}
                                >
                                  Remove
                                </button>
                              </span>
                            ) : null}
                          </figcaption>
                        </figure>
                      ))}
                    </>
                  ) : (
                    <div className="auth-runtime-note">
                      <p>No event images added yet.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="portal-event-editor-block portal-event-ticket-editor">
                <div className="portal-section-heading">
                  <div>
                    <p className="info-kicker">Ticket types</p>
                    <h3>General admission, vendors, and event-specific participation.</h3>
                  </div>
                  {canManageEvents ? (
                    <button
                      type="button"
                      onClick={() =>
                        setDraftEvent((current) =>
                          current
                            ? {
                                ...current,
                                tickets: [...current.tickets, createDraftTicket()],
                              }
                            : current,
                        )
                      }
                      className="button-secondary tap-target"
                    >
                      Add ticket type
                    </button>
                  ) : null}
                </div>

                <div className="portal-admin-ticket-list">
                  {draftEvent.tickets.map((ticket) => (
                    <article key={ticket.id} className="portal-admin-ticket-card">
                      <div className="portal-admin-ticket-grid">
                        <label className="auth-field">
                          <span>Name</span>
                          <input
                            className="auth-input"
                            type="text"
                            value={ticket.name}
                            onChange={(event) => updateDraftTicket(ticket.id, { name: event.target.value })}
                            disabled={!canManageEvents}
                          />
                        </label>

                        <label className="auth-field">
                          <span>Category</span>
                          <select
                            className="auth-input"
                            value={ticket.category}
                            onChange={(event) =>
                              updateDraftTicket(ticket.id, {
                                category: event.target.value as EventTicketType['category'],
                                participantType:
                                  event.target.value === 'participant'
                                    ? (ticket.participantType ?? 'general')
                                    : undefined,
                              })
                            }
                            disabled={!canManageEvents}
                          >
                            <option value="general">General</option>
                            <option value="vendor">Vendor</option>
                            <option value="resource-vendor">Resource Vendor</option>
                            <option value="participant">Participant</option>
                          </select>
                        </label>

                        {ticket.category === 'participant' ? (
                          <label className="auth-field">
                            <span>Participant type</span>
                            <select
                              className="auth-input"
                              value={ticket.participantType ?? 'general'}
                              onChange={(event) =>
                                updateDraftTicket(ticket.id, {
                                  participantType: event.target.value as EventTicketType['participantType'],
                                })
                              }
                              disabled={!canManageEvents}
                            >
                              <option value="general">General participant</option>
                              <option value="adult-model">Adult model</option>
                              <option value="child-model">Child model</option>
                            </select>
                          </label>
                        ) : null}

                        <label className="auth-field">
                          <span>Ticket status</span>
                          <select
                            className="auth-input"
                            value={ticket.status}
                            onChange={(event) =>
                              updateDraftTicket(ticket.id, {
                                status: event.target.value as EventTicketType['status'],
                              })
                            }
                            disabled={!canManageEvents}
                          >
                            <option value="coming-soon">Coming soon</option>
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                          </select>
                        </label>

                        <label className="auth-field">
                          <span>Price</span>
                          <input
                            className="auth-input"
                            type="number"
                            min="0"
                            step="1"
                            value={ticket.price ?? ''}
                            onChange={(event) =>
                              updateDraftTicket(ticket.id, {
                                price: event.target.value === '' ? null : Number(event.target.value),
                              })
                            }
                            disabled={!canManageEvents}
                          />
                        </label>

                        <label className="auth-field portal-field-span-full">
                          <span>Description</span>
                          <textarea
                            className="auth-input auth-input--textarea"
                            value={ticket.description}
                            onChange={(event) => updateDraftTicket(ticket.id, { description: event.target.value })}
                            disabled={!canManageEvents}
                            rows={3}
                          />
                        </label>
                      </div>

                      <div className="portal-admin-ticket-actions">
                        <span className="portal-price-preview">{ticket.priceLabel}</span>
                        {canManageEvents ? (
                          <button
                            type="button"
                            onClick={() =>
                              setDraftEvent((current) =>
                                current
                                  ? {
                                      ...current,
                                      tickets:
                                        current.tickets.length > 1
                                          ? current.tickets.filter((item) => item.id !== ticket.id)
                                          : current.tickets,
                                    }
                                  : current,
                              )
                            }
                            className="button-ghost tap-target"
                            disabled={draftEvent.tickets.length <= 1}
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              <div className="portal-event-editor-block">
                <div className="portal-section-heading portal-section-heading--compact">
                  <div>
                    <p className="info-kicker">Publish controls</p>
                    <h3>Save, publish, feature, or retire this event.</h3>
                  </div>
                </div>

                <div className="portal-admin-actions">
                  <div className="portal-inline-actions">
                    <span className={`portal-publish-badge ${draftEvent.isPublished ? 'portal-publish-badge--live' : ''}`}>
                      {draftEvent.isPublished ? 'Published' : 'Unpublished'}
                    </span>
                    {featuredEvent?.id === draftEvent.id ? (
                      <span className="portal-publish-badge portal-publish-badge--feature">Featured</span>
                    ) : null}
                  </div>

                  {canManageEvents ? (
                    <div className="portal-inline-actions">
                      <button type="button" onClick={handleSaveEvent} className="button-primary tap-target" disabled={isWorking}>
                        {isWorking ? 'Saving...' : 'Save event'}
                      </button>
                      <button type="button" onClick={handleTogglePublished} className="button-secondary tap-target" disabled={isWorking}>
                        {draftEvent.isPublished ? 'Unpublish event' : 'Publish event'}
                      </button>
                      <button type="button" onClick={handleFeatureEvent} className="button-ghost tap-target" disabled={isWorking}>
                        Make featured
                      </button>
                      <button type="button" onClick={handleDeleteEvent} className="button-ghost tap-target portal-danger-button" disabled={isWorking}>
                        Delete event
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <div className="auth-runtime-note">
              <p className="info-kicker">No events</p>
              <p>Create an event to begin building the published event flow.</p>
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}

function dedupeUploadedImages(images: UploadedEventImage[]): UploadedEventImage[] {
  const seen = new Set<string>();

  return images.filter((image) => {
    if (!image.url || seen.has(image.url)) {
      return false;
    }

    seen.add(image.url);
    return true;
  });
}
